import { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Calendar, ChevronLeft, ChevronRight, User, Clock, Eye, Grid3X3, X, ListChecks
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import AttendeeCardModal from "@/components/AttendeeCardModal"; // Iter 9.40 Pass I.1 - unify

const VIEWS = ["month", "week", "day"];

function getWeekDates(date) {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function getMonthDates(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay();
  const days = [];
  // pad start
  for (let i = startDay - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }
  for (let d = 1; d <= last.getDate(); d++) {
    days.push({ date: new Date(year, month, d), isCurrentMonth: true });
  }
  // pad end to fill 6 rows
  while (days.length < 42) {
    const d = new Date(year, month + 1, days.length - last.getDate() - startDay + 1);
    days.push({ date: d, isCurrentMonth: false });
  }
  return days;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function formatDate(d) {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function getImgUrl(img) {
  return typeof img === "string" ? img : (img?.url || img?.image_url || "");
}

function AppointmentCard({ rsvp, compact, onView, onQuickView }) {
  const inspo = rsvp.inspo_images || rsvp.inspiration_images || rsvp.inspo || [];
  const firstImg = inspo.length > 0 ? getImgUrl(inspo[0]) : null;
  const timeStr = rsvp.appointment_time || "";

  if (compact) {
    return (
      <div className="flex items-center gap-1 w-full px-1.5 py-1 rounded-md text-left hover:bg-purple-50 transition-colors group"
        data-testid={`cal-appt-${rsvp.rsvp_id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onView?.(rsvp); }}
          className="flex items-center gap-1.5 flex-1 min-w-0"
        >
          {firstImg ? (
            <img src={firstImg} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
          ) : (
            <div className="w-5 h-5 rounded bg-purple-100 flex items-center justify-center shrink-0">
              <User className="w-3 h-3 text-purple-500" />
            </div>
          )}
          <span className="text-[10px] font-medium text-gray-800 truncate">{rsvp.name?.split(" ")[0]}</span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onQuickView?.(rsvp); }}
          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-purple-100 transition-all shrink-0"
          data-testid={`cal-eye-${rsvp.rsvp_id}`}
          title="Quick view preferences"
        >
          <Eye className="w-3 h-3 text-purple-500" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all cursor-pointer group"
      data-testid={`cal-card-${rsvp.rsvp_id}`}
    >
      {/* Preview image or avatar */}
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 shrink-0" onClick={() => onView?.(rsvp)}>
        {firstImg ? (
          <img src={firstImg} alt="" className="w-full h-full object-cover" />
        ) : rsvp.profile_image ? (
          <img src={rsvp.profile_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-5 h-5 text-gray-400" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0" onClick={() => onView?.(rsvp)}>
        <p className="text-sm font-semibold text-gray-900 truncate">{rsvp.name}</p>
        {rsvp.service_name && (
          <p className="text-xs text-gray-500 truncate">{rsvp.service_name}</p>
        )}
        {rsvp.linked_experience_name && (
          <p className="text-[10px] text-purple-500 truncate">{rsvp.linked_experience_name}</p>
        )}
      </div>
      <div className="text-right shrink-0 flex items-center gap-2">
        <div>
          {timeStr && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />{timeStr}
            </p>
          )}
          <Badge variant="outline" className="text-[9px] mt-0.5 capitalize">
            {rsvp.appointment_stage || rsvp.status || "confirmed"}
          </Badge>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onQuickView?.(rsvp); }}
          className="p-1.5 rounded-lg hover:bg-purple-100 text-purple-500 hover:text-purple-700 transition-colors"
          data-testid={`cal-eye-day-${rsvp.rsvp_id}`}
          title="Quick view preferences"
        >
          <Eye className="w-4 h-4" />
        </button>
      </div>
      {/* Inspo previews on hover */}
      {inspo.length > 1 && (
        <div className="hidden group-hover:flex gap-1 shrink-0">
          {inspo.slice(0, 3).map((img, i) => (
            <img key={i} src={getImgUrl(img)} alt="" className="w-8 h-8 rounded object-cover" />
          ))}
        </div>
      )}
    </div>
  );
}
export default function AppointmentCalendar({ rsvps = [], onViewDetail }) {
  const [view, setView] = useState("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [quickViewRsvp, setQuickViewRsvp] = useState(null);

  // Parse dates from RSVPs
  const appointments = useMemo(() => {
    return (rsvps || []).filter(r => r.appointment_date).map(r => ({
      ...r,
      _date: new Date(r.appointment_date.split("T")[0] + "T12:00:00"),
    }));
  }, [rsvps]);

  const getAppointmentsForDate = (date) => {
    return appointments.filter(a => isSameDay(a._date, date));
  };

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7 * dir);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());
  const today = new Date();

  const handleView = (rsvp) => {
    setSelectedAppt(rsvp);
    onViewDetail?.(rsvp);
  };

  const handleQuickView = (rsvp) => {
    console.log("QUICKVIEW TRIGGERED for:", rsvp?.name);
    setQuickViewRsvp(rsvp);
  };

  // Header label
  const headerLabel = view === "month"
    ? currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : view === "week"
      ? (() => {
          const week = getWeekDates(currentDate);
          return `${week[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${week[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
        })()
      : formatDate(currentDate);

  return (
    <div className="space-y-3" data-testid="appointment-calendar">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-600" />
          <h3 className="text-sm font-semibold text-gray-900">{headerLabel}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={goToday} className="text-xs h-7">
            Today
          </Button>
          <div className="flex items-center border rounded-lg overflow-hidden">
            <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-gray-100" data-testid="cal-prev">
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => navigate(1)} className="p-1.5 hover:bg-gray-100" data-testid="cal-next">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex items-center border rounded-lg overflow-hidden text-xs">
            {VIEWS.map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-2 py-1.5 capitalize ${view === v ? "bg-purple-100 text-purple-700 font-medium" : "text-gray-500 hover:bg-gray-50"}`}
                data-testid={`cal-view-${v}`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Month View */}
      {view === "month" && (
        <div className="border rounded-xl overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50 border-b">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
              <div key={d} className="text-center text-[10px] font-medium text-gray-500 py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {getMonthDates(currentDate).map((day, i) => {
              const appts = getAppointmentsForDate(day.date);
              const isToday = isSameDay(day.date, today);
              return (
                <div
                  key={i}
                  className={`min-h-[72px] border-b border-r p-1 ${
                    !day.isCurrentMonth ? "bg-gray-50/50" : "bg-white"
                  } ${isToday ? "ring-1 ring-inset ring-purple-300" : ""}`}
                  onClick={() => { setCurrentDate(day.date); setView("day"); }}
                >
                  <span className={`text-[10px] font-medium ${
                    isToday ? "bg-purple-600 text-white w-5 h-5 rounded-full inline-flex items-center justify-center" :
                    day.isCurrentMonth ? "text-gray-700" : "text-gray-300"
                  }`}>
                    {day.date.getDate()}
                  </span>
                  <div className="mt-0.5 space-y-0.5">
                    {appts.slice(0, 2).map(a => (
                      <AppointmentCard key={a.rsvp_id} rsvp={a} compact onView={handleView} onQuickView={handleQuickView} />
                    ))}
                    {appts.length > 2 && (
                      <span className="text-[9px] text-purple-600 font-medium pl-1">+{appts.length - 2} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week View */}
      {view === "week" && (
        <div className="border rounded-xl overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50 border-b">
            {getWeekDates(currentDate).map((d, i) => {
              const isToday = isSameDay(d, today);
              return (
                <div key={i} className={`text-center py-2 ${isToday ? "bg-purple-50" : ""}`}>
                  <p className="text-[10px] text-gray-500">{d.toLocaleDateString("en-US", { weekday: "short" })}</p>
                  <p className={`text-sm font-semibold ${isToday ? "text-purple-700" : "text-gray-800"}`}>{d.getDate()}</p>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7 min-h-[300px]">
            {getWeekDates(currentDate).map((d, i) => {
              const appts = getAppointmentsForDate(d);
              const isToday = isSameDay(d, today);
              return (
                <div
                  key={i}
                  className={`border-r p-1.5 space-y-1.5 ${isToday ? "bg-purple-50/30" : ""}`}
                  onClick={() => { setCurrentDate(d); setView("day"); }}
                >
                  {appts.map(a => (
                    <AppointmentCard key={a.rsvp_id} rsvp={a} compact onView={handleView} onQuickView={handleQuickView} />
                  ))}
                  {appts.length === 0 && (
                    <div className="flex items-center justify-center h-full opacity-30">
                      <Grid3X3 className="w-4 h-4 text-gray-300" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day View */}
      {view === "day" && (
        <div className="space-y-2">
          <div className="bg-purple-50 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-gray-900">
              {currentDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </p>
            <p className="text-xs text-gray-500">
              {getAppointmentsForDate(currentDate).length} appointment{getAppointmentsForDate(currentDate).length !== 1 ? "s" : ""}
            </p>
          </div>
          {getAppointmentsForDate(currentDate).length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No appointments this day</p>
            </div>
          ) : (
            <div className="space-y-2">
              {getAppointmentsForDate(currentDate).map(a => (
                <AppointmentCard key={a.rsvp_id} rsvp={a} onView={handleView} onQuickView={handleQuickView} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Iter 9.40 Pass I.1 - UNIFIED preference card popup (AttendeeCardModal) */}
      <AttendeeCardModal
        open={!!quickViewRsvp}
        onOpenChange={(v) => !v && setQuickViewRsvp(null)}
        rsvp={quickViewRsvp}
        hostEventTitle={quickViewRsvp?.service_name || "Preference Card"}
        hostAccent="#7c3aed"
        hideHostControls={false}
      />
    </div>
  );
}
